import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import { Button, Input } from 'antd'
import { useRecoilValue } from 'recoil'
import { frontendDisplayedCourseSelector } from '@/atoms/auth'
import { saveContentCreatorNote } from '@/api/cb/contentCreatorContent'
import { ContentTypeForNotes } from 'common/src/api/contentFrontendVsContentBackend/contentCreatorNotes'
import { syncErrorMessage, syncSuccessMessage } from '@/utils/syncMessages'
import styles from './ContentCreatorNotes.module.less'

interface ContentCreatorNotesEditorProps {
    contentType: ContentTypeForNotes
    contentID: string
    initialNotes: string
    onNotesSaved?: (newNotes: string) => void
}

export const ContentCreatorNotesEditor: React.FC<
    ContentCreatorNotesEditorProps
> = (props): ReactElement => {
    const courseName = useRecoilValue(frontendDisplayedCourseSelector)
    const [notes, setNotes] = useState(props.initialNotes)
    const [savedNotes, setSavedNotes] = useState(props.initialNotes)
    const [isSaving, setIsSaving] = useState(false)

    useEffect(() => {
        setNotes(props.initialNotes)
        setSavedNotes(props.initialNotes)
    }, [props.initialNotes])

    const hasChanges = notes !== savedNotes

    useEffect(() => {
        if (!hasChanges) return
        const handler = (e: BeforeUnloadEvent): void => {
            e.preventDefault()
        }
        window.addEventListener('beforeunload', handler)
        return () => window.removeEventListener('beforeunload', handler)
    }, [hasChanges])

    const handleSave = useCallback(async (): Promise<void> => {
        setIsSaving(true)
        const response = await saveContentCreatorNote({
            courseName,
            contentType: props.contentType,
            contentID: props.contentID,
            notes,
        })
        setIsSaving(false)
        if (response.data.isError) {
            syncErrorMessage('Error saving note')
        } else {
            setSavedNotes(notes)
            syncSuccessMessage('Note saved')
            props.onNotesSaved?.(notes)
        }
    }, [
        notes,
        props.contentType,
        props.contentID,
        props.onNotesSaved,
        courseName,
    ])

    return (
        <div className={styles.contentCreatorNotesEditor}>
            <div className={styles.notesLabel}>My Notes</div>
            <Input.TextArea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes..."
                rows={4}
                className={styles.notesTextArea}
            />
            <div className={styles.saveButtonContainer}>
                <Button
                    type="primary"
                    onClick={handleSave}
                    disabled={!hasChanges}
                    loading={isSaving}
                    size="small"
                >
                    Save
                </Button>
            </div>
        </div>
    )
}
